/**
 * Module de personnalisation — Murmures d'Iqra
 * ---------------------------------------------------------------------------
 * assets/personnalisation-module.js
 * Pilote le wizard multi-étapes rendu par sections/personnalisation-module.liquid
 * (4 parcours combinables : étiquettes/cartes de visite, visuel personnel POD,
 * style visuel pour produits digitaux, design de la galerie) + parcours de
 * secours (hors périmètre, modération halal, "avec échanges").
 *
 * Fonctionnalité ajoutée (v26 — scan visage côté navigateur) :
 *  - Détection indicative de visage (face-api.js, tiny_face_detector) sur
 *    toute image envoyée (logo étiquette recto/verso, visuel POD). Jamais
 *    bloquant : ajoute juste une note visuelle + un attribut de commande
 *    "Scan visage (auto) : détecté — à vérifier" pour pré-trier la revue
 *    manuelle. Ne remplace pas la case à cocher "Confirmation absence
 *    visage", s'y ajoute.
 *
 * Fonctionnalité ajoutée (v25 — mini-aperçu visuel dans le récap) :
 *  - buildRecapVisual() construit une petite carte visuelle au-dessus du
 *    récapitulatif texte : image produit + texte/logo pour étiquette/CDV,
 *    aperçu du visuel envoyé pour POD, dégradé + texte stylé pour digital.
 *    Purement additif à l'affichage, aucun impact sur le panier/prix.
 *
 * Améliorations (v24 — filtres galerie, lien design/format) :
 *  - Filtrage par catégorie (chips) + recherche texte dans la grille de
 *    designs, avec compteur de résultats.
 *  - Réglage optionnel par design "Formats compatibles" (tags produit) :
 *    les designs incompatibles avec le support choisi sont masqués par
 *    défaut, avec une case à cocher pour les révéler.
 * ---------------------------------------------------------------------------
 */
(function () {
  "use strict";

  var DRAFT_TTL_MS = 1000 * 60 * 60 * 24 * 3;

  var MOTS_SENSIBLES = [
    "coran", "quran", "qur'an", "hadith", "sourate", "sourate", "verset",
    "ayat", "ayah", "bismillah au complet", "قرآن", "حديث", "آية", "سورة"
  ];

  function initModule(root) {
    if (root.dataset.persInitialized === "true") return;
    root.dataset.persInitialized = "true";

    var i18nEl = root.querySelector("[data-pers-i18n]");
    var strings = {};
    try { strings = JSON.parse(i18nEl.textContent); } catch (e) { }

    function t(cle, remplacements) {
      var texte = strings[cle] || cle;
      if (remplacements) {
        Object.keys(remplacements).forEach(function (k) {
          texte = texte.replace("{" + k + "}", remplacements[k]);
          texte = texte.replace("{{ " + k + " }}", remplacements[k]);
        });
      }
      return texte;
    }

    var data = root.dataset;
    var currency = data.currency || "EUR";
    var maxUploadMb = parseFloat(data.maxUploadMb || "15");
    var dpiMinimum = parseFloat(data.dpiMinimum || "150");
    var notifyEmail = data.notifyEmail || "";
    var makeWebhookUrl = data.makeWebhookUrl || "";
    var devisWebhookUrl = data.devisWebhookUrl || "";

    var halalMotsClesParLangue = {};
    try {
      var halalMotsClesScript = root.querySelector("[data-pers-halal-mots-cles]");
      if (halalMotsClesScript) { halalMotsClesParLangue = JSON.parse(halalMotsClesScript.textContent || "{}"); }
    } catch (e) { halalMotsClesParLangue = {}; }
    var halalLangue = (document.documentElement.getAttribute("lang") || "en").toLowerCase().slice(0, 2);
    var halalMotsClesGeneral = (halalMotsClesParLangue[halalLangue] || halalMotsClesParLangue.en || [])
      .map(function (m) { return normaliserTexte((m || "").trim()); }).filter(Boolean);

    function normaliserTexte(txt) { return (txt || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""); }

    function texteContientMot(texte, listeMots) {
      if (!listeMots.length || !texte) return false;
      var normalise = normaliserTexte(texte);
      return listeMots.some(function (mot) {
        if (!mot) return false;
        var motEchappe = mot.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        var regex = new RegExp("(^|[^a-z0-9])" + motEchappe + "([^a-z0-9]|$)");
        return regex.test(normalise);
      });
    }

    function contientContenuHalalGeneral() {
      var textes = [state.etiquette.lines.join(" "), state.etiquette.versoLines.join(" "), state.etiquette.instructions, state.pod.note, state.digital.name, state.digital.dedication, state.digital.note, state.digital.fullNote, state.gallery.note].join(" ");
      return texteContientMot(textes, halalMotsClesGeneral);
    }

    var services = {
      print: { variantId: data.printVariantId || "", priceCents: parseFloat(data.printPriceCents || "0"), priceFormatted: data.printPriceFormatted || "" },
      podStandard: { variantId: data.podStandardVariantId || "", priceCents: parseFloat(data.podStandardPriceCents || "0"), priceFormatted: data.podStandardPriceFormatted || "" },
      podAdvanced: { variantId: data.podAdvancedVariantId || "", priceCents: parseFloat(data.podAdvancedPriceCents || "0"), priceFormatted: data.podAdvancedPriceFormatted || "" },
      formatAdapt: { variantId: data.formatAdaptVariantId || "", priceCents: parseFloat(data.formatAdaptPriceCents || "0"), priceFormatted: data.formatAdaptPriceFormatted || "" },
      coverColor: { variantId: data.coverColorVariantId || "", priceCents: parseFloat(data.coverColorPriceCents || "0"), priceFormatted: data.coverColorPriceFormatted || "" }
    };

    var el = {
      progress: root.querySelector("#pers-progress"), progressLabel: root.querySelector("#pers-progress-label"), backBtn: root.querySelector("#pers-back-btn"),
      step1: root.querySelector('[data-step="1"]'), cards: root.querySelectorAll(".pers-card[data-flow]"), oosTrigger: root.querySelector("#pers-trigger-oos"),
      flowPanels: root.querySelectorAll("[data-flow-panel]"), errorBanner: root.querySelector("#pers-error-banner"), successBanner: root.querySelector("#pers-success-banner"),
      recapVisual: root.querySelector("#pers-recap-visual"),
      recapContent: root.querySelector("#pers-recap-content"), priceTotal: root.querySelector("#pers-price-total"), confirmAccuracy: root.querySelector("#pers-confirm-accuracy"),
      confirmAccuracyLabel: root.querySelector("#pers-confirm-accuracy-label"), addToCartBtn: root.querySelector("#pers-add-to-cart"), halalConfirm: root.querySelector("#pers-halal-confirm"),
      halalDetailsToggle: root.querySelector("[data-pers-halal-details-toggle]"), halalDetails: root.querySelector("[data-pers-halal-details]"), halalDoubtBtn: root.querySelector("[data-pers-halal-doubt]")
    };

    var state = {
      flow: null, history: [], currentStep: "1", locked: false,
      etiquette: { product: null, lines: ["", "", ""], logo: null, instructions: "", service: "fichier_seul", formatAdapt: false, echanges: false, hasVerso: false, activeSide: "recto", versoLines: ["", "", ""], versoLogo: null },
      pod: { product: null, file: null, note: "", complexity: "standard" },
      digital: { product: null, scope: "couverture", palette: "origine", font: "Cormorant Garamond", size: "Confortable", name: "", dedication: "", hijri: false, note: "", fullNote: "" },
      gallery: { product: null, design: null, note: "" }
    };

    function formaterPrix(montant) {
      try { return new Intl.NumberFormat(document.documentElement.lang || "fr-FR", { style: "currency", currency: currency }).format(montant); }
      catch (e) { return montant.toFixed(2) + " " + currency; }
    }

    if (el.errorBanner) { el.errorBanner.setAttribute("role", "alert"); el.errorBanner.setAttribute("aria-live", "assertive"); }
    if (el.successBanner) { el.successBanner.setAttribute("role", "status"); el.successBanner.setAttribute("aria-live", "polite"); }

    function afficherErreur(message) {
      if (!el.errorBanner) return;
      el.errorBanner.textContent = message; el.errorBanner.hidden = false;
      el.errorBanner.scrollIntoView({ behavior: "smooth", block: "center" });
      if (el.successBanner) el.successBanner.hidden = true;
    }
    function masquerErreur() { if (el.errorBanner) el.errorBanner.hidden = true; }
    function signalerChampInvalide(champ) {
      if (!champ) return;
      var conteneur = champ.closest(".pers-field") || champ;
      conteneur.classList.add("pers-field--invalid"); champ.setAttribute("aria-invalid", "true");
      var nettoyer = function () {
        conteneur.classList.remove("pers-field--invalid"); champ.removeAttribute("aria-invalid");
        champ.removeEventListener("input", nettoyer); champ.removeEventListener("change", nettoyer);
      };
      champ.addEventListener("input", nettoyer); champ.addEventListener("change", nettoyer);
    }

    function afficherSucces(html) {
      if (!el.successBanner) return;
      el.successBanner.innerHTML = html; el.successBanner.hidden = false; masquerErreur();
    }

    function contientContenuSensible() {
      var textes = [state.etiquette.lines.join(" "), state.etiquette.versoLines.join(" "), state.etiquette.instructions, state.pod.note].join(" ").toLowerCase();
      return MOTS_SENSIBLES.some(function (mot) { return textes.indexOf(mot.toLowerCase()) !== -1; });
    }

    function mailto(sujet, corps) { return "mailto:" + encodeURIComponent(notifyEmail) + "?subject=" + encodeURIComponent(sujet) + "&body=" + encodeURIComponent(corps); }

    function lireContactOptin(prefix) {
      var email = root.querySelector("#pers-contact-email-" + prefix);
      var whatsapp = root.querySelector("#pers-contact-whatsapp-" + prefix);
      var telegram = root.querySelector("#pers-contact-telegram-" + prefix);
      var out = {};
      if (email && email.value.trim()) out.email = email.value.trim();
      if (whatsapp && whatsapp.value.trim()) out.whatsapp = whatsapp.value.trim();
      if (telegram && telegram.value.trim()) out.telegram = telegram.value.trim();
      return out;
    }

    function formaterContact(contact) {
      var lignes = [];
      if (contact.email) lignes.push("E-mail : " + contact.email);
      if (contact.whatsapp) lignes.push("WhatsApp : " + contact.whatsapp);
      if (contact.telegram) lignes.push("Telegram : " + contact.telegram);
      return lignes.length ? lignes.join("\n") : "(aucune coordonnée laissée)";
    }

    function envoyerDevisSiPossible(payload) {
      if (!devisWebhookUrl) return Promise.resolve(false);
      var controleur = (typeof AbortController !== "undefined") ? new AbortController() : null;
      var minuteur = controleur ? setTimeout(function () { controleur.abort(); }, 10000) : null;
      return fetch(devisWebhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controleur ? controleur.signal : undefined
      })
        .then(function (res) { return !!(res && res.ok); })
        .catch(function () { return false; })
        .finally(function () { if (minuteur) clearTimeout(minuteur); });
    }

    var flowStepOrders = { "etiquette-cdv": ["2a", "3a", "4a", "recap"], "pod-image": ["2b", "3b", "recap"], "digital-style": ["2c", "3c", "recap"], "gallery-design": ["2d", "3d"] };
    var flowPanelOf = { "2a": "etiquette-cdv", "3a": "etiquette-cdv", "4a": "etiquette-cdv", "2b": "pod-image", "3b": "pod-image", "2c": "digital-style", "3c": "digital-style", "2d": "gallery-design", "3d": "gallery-design" };

    function tousLesSteps() { return root.querySelectorAll("[data-step]"); }

    function showStep(stepId, options) {
      options = options || {};
      if (!options.viaBack) { state.history.push(state.currentStep); }
      state.currentStep = stepId; masquerErreur();
      if (el.step1) el.step1.hidden = true;
      el.flowPanels.forEach(function (p) { p.hidden = true; });
      tousLesSteps().forEach(function (s) { s.hidden = true; });
      if (stepId === "1") { if (el.step1) el.step1.hidden = false; if (el.progress) el.progress.hidden = true; return; }
      var panel = flowPanelOf[stepId];
      if (panel) { var panelEl = root.querySelector('[data-flow-panel="' + panel + '"]'); if (panelEl) panelEl.hidden = false; }
      var stepEl = root.querySelector('[data-step="' + stepId + '"]');
      if (stepEl) stepEl.hidden = false;
      if (stepEl) {
        var cible = stepEl.querySelector("h1,h2,h3,[tabindex]") || stepEl;
        if (!cible.hasAttribute("tabindex")) cible.setAttribute("tabindex", "-1");
        window.requestAnimationFrame(function () { cible.focus({ preventScroll: true }); cible.scrollIntoView({ behavior: "smooth", block: "start" }); });
      }
      var ordre = state.flow ? flowStepOrders[state.flow] : null;
      if (el.progress) {
        if (ordre && ordre.indexOf(stepId) !== -1) {
          el.progress.hidden = false;
          if (el.progressLabel) el.progressLabel.textContent = t("step_of", { n: ordre.indexOf(stepId) + 1, total: ordre.length });
        } else if (["oos", "moderation", "digital-full-confirm", "etiquette-echanges-confirm", "gallery-confirm"].indexOf(stepId) !== -1) {
          el.progress.hidden = false; if (el.progressLabel) el.progressLabel.textContent = "";
        } else { el.progress.hidden = true; }
      }
      if (el.backBtn) el.backBtn.hidden = false;
      if (stepId === "recap") buildRecap();
      sauvegarderBrouillon();
    }

    function retourAuxChoix() {
      state.history = []; state.currentStep = "1"; state.flow = null;
      el.cards.forEach(function (c) { c.setAttribute("aria-pressed", "false"); });
      showStep("1", { viaBack: true });
    }

    if (el.backBtn) {
      el.backBtn.addEventListener("click", function () {
        var precedent = state.history.pop();
        if (precedent === undefined || precedent === "1") { retourAuxChoix(); } else { showStep(precedent, { viaBack: true }); }
      });
    }

    el.cards.forEach(function (card) {
      card.addEventListener("click", function () {
        el.cards.forEach(function (c) { c.setAttribute("aria-pressed", c === card ? "true" : "false"); });
        state.flow = card.dataset.flow; state.history = ["1"];
        var premiereEtape = { "etiquette-cdv": "2a", "pod-image": "2b", "digital-style": "2c", "gallery-design": "2d" }[state.flow];
        showStep(premiereEtape, { viaBack: true });
      });
    });

    if (el.oosTrigger) {
      el.oosTrigger.addEventListener("click", function () { el.oosTrigger.setAttribute("aria-expanded", "true"); state.history = ["1"]; showStep("oos", { viaBack: true }); });
    }

    root.querySelectorAll("[data-counter-target]").forEach(function (champ) {
      var cible = root.querySelector("#" + champ.dataset.counterTarget);
      if (!cible) return;
      function majCompteur() {
        var max = parseInt(champ.getAttribute("maxlength") || "0", 10);
        var restant = max - champ.value.length;
        cible.textContent = restant + " " + t("chars_remaining");
        cible.classList.toggle("is-low", max > 0 && restant <= Math.min(10, Math.ceil(max * 0.1)));
      }
      champ.addEventListener("input", majCompteur); majCompteur();
    });

    function activerDropzone(input) {
      var zone = input.closest(".pers-field") || input.parentElement;
      if (!zone || zone.dataset.dropzoneReady === "true") return;
      zone.dataset.dropzoneReady = "true"; zone.classList.add("pers-dropzone");
      ["dragenter", "dragover"].forEach(function (evt) { zone.addEventListener(evt, function (e) { e.preventDefault(); zone.classList.add("is-dragover"); }); });
      ["dragleave", "drop"].forEach(function (evt) { zone.addEventListener(evt, function (e) { e.preventDefault(); zone.classList.remove("is-dragover"); }); });
      zone.addEventListener("drop", function (e) {
        var fichiers = e.dataTransfer && e.dataTransfer.files;
        if (fichiers && fichiers.length) { input.files = fichiers; input.dispatchEvent(new Event("change")); }
      });
    }

    function traiterFichier(fichier, opts) {
      return new Promise(function (resolve, reject) {
        var tailleMo = fichier.size / (1024 * 1024);
        if (tailleMo > maxUploadMb) { reject({ code: "trop_lourd", message: t("error_file_too_large", { max: maxUploadMb }) }); return; }
        var estImage = /^image\/(png|jpeg)$/.test(fichier.type);
        var estVectoriel = fichier.type === "image/svg+xml" || fichier.type === "application/pdf" || /\.(svg|pdf)$/i.test(fichier.name);
        if (!estImage && !estVectoriel) { reject({ code: "type_invalide", message: t("error_file_type") }); return; }
        if (estVectoriel && !opts.acceptVector) { reject({ code: "type_invalide", message: t("error_file_type") }); return; }
        if (estVectoriel) { resolve({ kind: "vector", file: fichier, dataUrl: null, widthPx: null, heightPx: null, dpi: null, transform: { x: 0, y: 0, scale: 1, rotation: 0 } }); return; }
        var lecteur = new FileReader();
        lecteur.onload = function (evt) {
          var img = new Image();
          img.onload = function () {
            var dpi = null;
            if (opts.largeurMm && opts.hauteurMm) {
              var dpiLargeur = img.naturalWidth / (opts.largeurMm / 25.4);
              var dpiHauteur = img.naturalHeight / (opts.hauteurMm / 25.4);
              dpi = Math.round(Math.min(dpiLargeur, dpiHauteur));
            }
            resolve({ kind: "image", file: fichier, dataUrl: evt.target.result, widthPx: img.naturalWidth, heightPx: img.naturalHeight, dpi: dpi, transform: { x: 0, y: 0, scale: 1, rotation: 0 } });
          };
          img.onerror = function () { reject({ code: "lecture_impossible", message: t("error_file_type") }); };
          img.src = evt.target.result;
        };
        lecteur.onerror = function () { reject({ code: "lecture_impossible", message: t("error_file_type") }); };
        lecteur.readAsDataURL(fichier);
      });
    }

    function afficherApercuFichier(previewRootEl, fichierState, onSupprimer) {
      previewRootEl.innerHTML = "";
      if (fichierState.kind === "image") {
        var wrap = document.createElement("div"); wrap.className = "pers-preview-thumb-wrap";
        var img = document.createElement("img"); img.className = "pers-preview-thumb"; img.src = fichierState.dataUrl; img.alt = t("canvas_alt") || "";
        wrap.appendChild(img); previewRootEl.appendChild(wrap);
        function appliquerTransform() {
          var tr = fichierState.transform;
          img.style.transform = "translate(" + tr.x + "px," + tr.y + "px) scale(" + tr.scale + ") rotate(" + tr.rotation + "deg)";
        }
        appliquerTransform();
        var controls = document.createElement("div"); controls.className = "pers-image-controls";
        controls.innerHTML =
          '<label>' + (t("zoom") || "Zoom") + ' <input type="range" min="0.5" max="2.5" step="0.05" value="1" data-ctrl="scale"></label>' +
          '<label>' + (t("rotation") || "Rotation") + ' <input type="range" min="-45" max="45" step="1" value="0" data-ctrl="rotation"></label>' +
          '<div class="pers-file-actions">' +
          '<button type="button" class="pers-btn-link" data-action="reset">' + (t("reset_placement") || "Réinitialiser le cadrage") + '</button>' +
          '<button type="button" class="pers-btn-link pers-btn-link--danger" data-action="remove">' + (t("remove_file") || "Supprimer ce fichier") + '</button>' +
          '</div>';
        previewRootEl.appendChild(controls);
        var champScale = controls.querySelector('[data-ctrl="scale"]');
        var champRotation = controls.querySelector('[data-ctrl="rotation"]');
        champScale.addEventListener("input", function (e) { fichierState.transform.scale = parseFloat(e.target.value); appliquerTransform(); });
        champRotation.addEventListener("input", function (e) { fichierState.transform.rotation = parseFloat(e.target.value); appliquerTransform(); });
        controls.querySelector('[data-action="reset"]').addEventListener("click", function () {
          fichierState.transform = { x: 0, y: 0, scale: 1, rotation: 0 }; champScale.value = 1; champRotation.value = 0; appliquerTransform();
        });
        controls.querySelector('[data-action="remove"]').addEventListener("click", function () { previewRootEl.innerHTML = ""; if (typeof onSupprimer === "function") onSupprimer(); });
        var glisse = false, depart = { x: 0, y: 0 };
        wrap.addEventListener("pointerdown", function (e) { glisse = true; depart = { x: e.clientX - fichierState.transform.x, y: e.clientY - fichierState.transform.y }; wrap.setPointerCapture(e.pointerId); });
        wrap.addEventListener("pointermove", function (e) { if (!glisse) return; fichierState.transform.x = e.clientX - depart.x; fichierState.transform.y = e.clientY - depart.y; appliquerTransform(); });
        wrap.addEventListener("pointerup", function () { glisse = false; });
      } else {
        var chip = document.createElement("div"); chip.className = "pers-file-chip";
        chip.innerHTML = "📎 <span>" + (fichierState.file ? fichierState.file.name : "") + "</span> " +
          '<button type="button" class="pers-btn-link pers-btn-link--danger" data-action="remove">' + (t("remove_file") || "Supprimer") + '</button>';
        previewRootEl.appendChild(chip);
        chip.querySelector('[data-action="remove"]').addEventListener("click", function () { previewRootEl.innerHTML = ""; if (typeof onSupprimer === "function") onSupprimer(); });
      }
    }

    function afficherNoteDpi(noteEl, fichierState) {
      if (!noteEl) return;
      if (fichierState.kind === "vector") { noteEl.hidden = false; noteEl.className = "pers-field__dpi-note pers-dpi-note"; noteEl.textContent = t("dpi_warning"); return; }
      if (fichierState.dpi == null) { noteEl.hidden = true; return; }
      noteEl.hidden = false;
      if (fichierState.dpi < dpiMinimum) { noteEl.className = "pers-field__dpi-note pers-dpi-note pers-dpi-note--warn"; noteEl.textContent = t("dpi_warning") + " (" + fichierState.dpi + " DPI)"; }
      else { noteEl.className = "pers-field__dpi-note pers-dpi-note pers-dpi-note--ok"; noteEl.textContent = t("dpi_ok") + " (" + fichierState.dpi + " DPI)"; }
    }

    // --- Scan visage côté navigateur (indicatif, jamais bloquant) ---
    // Détection légère via face-api.js (tiny_face_detector), chargée uniquement au premier
    // upload d'une image. Le résultat ne bloque jamais l'ajout au panier : il sert uniquement
    // à pré-trier les commandes à vérifier manuellement (attribut de commande + note visuelle).
    var faceApiLoadPromise = null;
    function chargerFaceApi() {
      if (faceApiLoadPromise) return faceApiLoadPromise;
      faceApiLoadPromise = new Promise(function (resolve, reject) {
        if (window.faceapi) { resolve(window.faceapi); return; }
        var script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
        script.onload = function () {
          window.faceapi.nets.tinyFaceDetector.loadFromUri("https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights")
            .then(function () { resolve(window.faceapi); })
            .catch(function (e) { reject(e); });
        };
        script.onerror = function (e) { reject(e); };
        document.head.appendChild(script);
      });
      return faceApiLoadPromise;
    }
    function scannerVisage(fichierState) {
      if (!fichierState || fichierState.kind !== "image") { return Promise.resolve("indetermine"); }
      return chargerFaceApi().then(function (faceapi) {
        return new Promise(function (resolve) {
          var img = new Image();
          img.onload = function () {
            faceapi.detectAllFaces(img, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
              .then(function (detections) { resolve(detections && detections.length > 0 ? "oui" : "non"); })
              .catch(function () { resolve("indetermine"); });
          };
          img.onerror = function () { resolve("indetermine"); };
          img.src = fichierState.dataUrl;
        });
      }).catch(function () { return "indetermine"; });
    }
    function afficherNoteScanVisage(previewRootEl, fichierState) {
      if (!previewRootEl) return;
      var ancienne = previewRootEl.querySelector(".pers-face-scan-note");
      if (ancienne) ancienne.remove();
      if (!fichierState || fichierState.faceScan !== "oui") return;
      var note = document.createElement("p");
      note.className = "pers-field__dpi-note pers-dpi-note pers-dpi-note--warn pers-face-scan-note";
      note.textContent = t("js_face_scan_detected") || "Un visage semble présent sur cette image — elle sera vérifiée manuellement avant impression, conformément à nos valeurs.";
      previewRootEl.appendChild(note);
    }
    function lancerScanVisage(fichierState, previewRootEl) {
      if (!fichierState || fichierState.kind !== "image") return;
      fichierState.faceScan = "en_cours";
      scannerVisage(fichierState).then(function (resultat) {
        fichierState.faceScan = resultat;
        afficherNoteScanVisage(previewRootEl, fichierState);
      });
    }

    (function initEtiquette() {
      var selectA = root.querySelector("#pers-product-select-a");
      var next2a = root.querySelector('.pers-step-next[data-next-step="3a"]');
      var sideToggle = root.querySelector("#pers-side-toggle");
      var faceRecto = root.querySelector("#pers-face-recto");
      var faceVerso = root.querySelector("#pers-face-verso");

      function definirFace(face) {
        state.etiquette.activeSide = face;
        if (faceRecto) faceRecto.hidden = face !== "recto";
        if (faceVerso) faceVerso.hidden = face !== "verso";
        if (sideToggle) { sideToggle.querySelectorAll(".pers-side-btn").forEach(function (b) { b.classList.toggle("is-active", b.dataset.side === face); }); }
      }
      if (sideToggle) { sideToggle.querySelectorAll(".pers-side-btn").forEach(function (b) { b.addEventListener("click", function () { definirFace(b.dataset.side); }); }); }

      if (selectA) {
        selectA.addEventListener("change", function () {
          var opt = selectA.selectedOptions[0];
          if (selectA.value) {
            state.etiquette.product = { variantId: selectA.value, productId: opt.dataset.productId, handle: opt.dataset.handle, widthMm: parseFloat(opt.dataset.widthMm || "0"), heightMm: parseFloat(opt.dataset.heightMm || "0"), title: opt.textContent, image: opt.dataset.image || "" };
            state.etiquette.hasVerso = opt.dataset.hasVerso === "true";
            if (sideToggle) sideToggle.hidden = !state.etiquette.hasVerso;
            definirFace("recto");
            if (next2a) next2a.disabled = false;
          } else { state.etiquette.product = null; state.etiquette.hasVerso = false; if (next2a) next2a.disabled = true; }
        });
      }
      if (next2a) { next2a.addEventListener("click", function () { if (!state.etiquette.product) { afficherErreur(t("error_select_product")); signalerChampInvalide(selectA); return; } showStep("3a"); }); }

      ["pers-text-1", "pers-text-2", "pers-text-3"].forEach(function (id, idx) {
        var champ = root.querySelector("#" + id); if (!champ) return;
        champ.addEventListener("input", function () { state.etiquette.lines[idx] = champ.value; });
      });
      ["pers-text-verso-1", "pers-text-verso-2", "pers-text-verso-3"].forEach(function (id, idx) {
        var champ = root.querySelector("#" + id); if (!champ) return;
        champ.addEventListener("input", function () { state.etiquette.versoLines[idx] = champ.value; });
      });

      var uploadLogo = root.querySelector("#pers-logo-upload");
      var previewA = root.querySelector("#pers-preview-canvas-a");
      var dpiNoteA = root.querySelector("#pers-dpi-note-a");
      if (uploadLogo) {
        activerDropzone(uploadLogo);
        uploadLogo.addEventListener("change", function () {
          var fichier = uploadLogo.files && uploadLogo.files[0];
          if (!fichier) { state.etiquette.logo = null; return; }
          var p = state.etiquette.product || {};
          traiterFichier(fichier, { largeurMm: p.widthMm, hauteurMm: p.heightMm, acceptVector: true })
            .then(function (res) {
              state.etiquette.logo = res;
              if (previewA) afficherApercuFichier(previewA, res, function () { state.etiquette.logo = null; uploadLogo.value = ""; if (dpiNoteA) dpiNoteA.hidden = true; });
              afficherNoteDpi(dpiNoteA, res);
              if (previewA) lancerScanVisage(res, previewA);
              if (res.kind === "image" && typeof afficherPaletteExtraite === "function") { afficherPaletteExtraite("pers-palette-extraite-a", res.dataUrl); }
            })
            .catch(function (err) { afficherErreur(err.message); uploadLogo.value = ""; });
        });
      }

      var uploadLogoVerso = root.querySelector("#pers-logo-upload-verso");
      var previewAVerso = root.querySelector("#pers-preview-canvas-a-verso");
      var dpiNoteAVerso = root.querySelector("#pers-dpi-note-a-verso");
      if (uploadLogoVerso) {
        activerDropzone(uploadLogoVerso);
        uploadLogoVerso.addEventListener("change", function () {
          var fichier = uploadLogoVerso.files && uploadLogoVerso.files[0];
          if (!fichier) { state.etiquette.versoLogo = null; return; }
          var p = state.etiquette.product || {};
          traiterFichier(fichier, { largeurMm: p.widthMm, hauteurMm: p.heightMm, acceptVector: true })
            .then(function (res) {
              state.etiquette.versoLogo = res;
              if (previewAVerso) afficherApercuFichier(previewAVerso, res, function () { state.etiquette.versoLogo = null; uploadLogoVerso.value = ""; if (dpiNoteAVerso) dpiNoteAVerso.hidden = true; });
              afficherNoteDpi(dpiNoteAVerso, res);
              if (previewAVerso) lancerScanVisage(res, previewAVerso);
              if (res.kind === "image" && typeof afficherPaletteExtraite === "function") { afficherPaletteExtraite("pers-palette-extraite-a-verso", res.dataUrl); }
            })
            .catch(function (err) { afficherErreur(err.message); uploadLogoVerso.value = ""; });
        });
      }

      var next3a = root.querySelector('.pers-step-next[data-next-step="4a"]');
      if (next3a) {
        next3a.addEventListener("click", function () {
          var auMoinsUneLigne = state.etiquette.lines.some(function (l) { return l && l.trim(); });
          if (!auMoinsUneLigne) { afficherErreur(t("error_missing_fields")); signalerChampInvalide(root.querySelector("#pers-text-1")); return; }
          showStep("4a");
        });
      }

      var radiosServiceA = root.querySelectorAll('input[name="pers-service-a"]');
      var prixImpressionSpan = root.querySelector('[data-price-from="print-service"]');
      var checkboxFormatAdapt = root.querySelector("#pers-format-adapt");
      var prixFormatAdaptSpan = root.querySelector('[data-price-from="format-adapt"]');
      var checkboxEchanges = root.querySelector("#pers-etiquette-echanges");

      function majAffichagePrixA() {
        if (prixImpressionSpan) prixImpressionSpan.textContent = "+" + services.print.priceFormatted;
        if (prixFormatAdaptSpan) {
          if (state.etiquette.service === "impression_envoi") { prixFormatAdaptSpan.textContent = t("included"); }
          else { prixFormatAdaptSpan.textContent = "+" + services.formatAdapt.priceFormatted; }
        }
      }
      radiosServiceA.forEach(function (r) { r.addEventListener("change", function () { if (r.checked) { state.etiquette.service = r.value; majAffichagePrixA(); } }); });
      if (checkboxFormatAdapt) { checkboxFormatAdapt.addEventListener("change", function () { state.etiquette.formatAdapt = checkboxFormatAdapt.checked; }); }
      if (checkboxEchanges) { checkboxEchanges.addEventListener("change", function () { state.etiquette.echanges = checkboxEchanges.checked; }); }
      majAffichagePrixA();

      var panelA = root.querySelector('[data-flow-panel="etiquette-cdv"]');
      if (panelA) {
        var boutonRecapA = panelA.querySelector('[data-step="4a"] .pers-step-next[data-next-step="recap"]');
        if (boutonRecapA) {
          boutonRecapA.addEventListener("click", function () {
            if (state.etiquette.echanges) { buildEchangesConfirm(); showStep("etiquette-echanges-confirm"); return; }
            if (contientContenuSensible()) { buildModeration("verse"); showStep("moderation"); return; }
            if (contientContenuHalalGeneral()) { buildModeration("general"); showStep("moderation"); return; }
            showStep("recap");
          });
        }
      }
    })();

    function buildEchangesConfirm() {
      var lien = root.querySelector("#pers-echanges-mailto");
      var messageEl = root.querySelector("#pers-echanges-confirm-message");
      if (messageEl) messageEl.textContent = t("echanges_confirm_message");
      if (!lien) return;
      var contact = lireContactOptin("echanges");
      var corps = "Bonjour,\n\nJe souhaiterais un aperçu avant validation finale pour ma personnalisation " +
        "étiquette / carte de visite :\n\nArticle : " + (state.etiquette.product ? state.etiquette.product.title : "—") +
        "\nTexte (recto) : " + state.etiquette.lines.filter(Boolean).join(" / ") +
        (state.etiquette.hasVerso ? ("\nTexte (verso) : " + state.etiquette.versoLines.filter(Boolean).join(" / ")) : "") +
        "\n\nCoordonnées de contact :\n" + formaterContact(contact) + "\n\nMerci !";
      lien.href = mailto(t("echanges_mailto_subject"), corps);

      envoyerDevisSiPossible({
        type: "echanges_etiquette_cdv",
        reference: "DEVIS-" + Date.now().toString(36).toUpperCase(),
        article: state.etiquette.product ? state.etiquette.product.title : null,
        texte_recto: state.etiquette.lines.filter(Boolean),
        texte_verso: state.etiquette.hasVerso ? state.etiquette.versoLines.filter(Boolean) : [],
        contact: contact,
        langue: document.documentElement.getAttribute("lang") || null
      }).then(function (succes) {
        if (succes && messageEl) { messageEl.textContent = t("echanges_confirm_message_auto"); }
      });
    }

    (function initPod() {
      var selectB = root.querySelector("#pers-product-select-b");
      var next2b = root.querySelector('.pers-step-next[data-next-step="3b"]');
      if (selectB) {
        selectB.addEventListener("change", function () {
          var opt = selectB.selectedOptions[0];
          if (selectB.value) {
            state.pod.product = { variantId: selectB.value, productId: opt.dataset.productId, handle: opt.dataset.handle, widthMm: parseFloat(opt.dataset.widthMm || "0"), heightMm: parseFloat(opt.dataset.heightMm || "0"), title: opt.textContent, image: opt.dataset.image || "" };
            if (next2b) next2b.disabled = false;
          } else { state.pod.product = null; if (next2b) next2b.disabled = true; }
        });
      }
      if (next2b) { next2b.addEventListener("click", function () { if (!state.pod.product) { afficherErreur(t("error_select_product")); signalerChampInvalide(selectB); return; } showStep("3b"); }); }

      var uploadPod = root.querySelector("#pers-pod-upload");
      var previewB = root.querySelector("#pers-preview-canvas-b");
      var dpiNoteB = root.querySelector("#pers-dpi-note-b");
      if (uploadPod) {
        activerDropzone(uploadPod);
        uploadPod.addEventListener("change", function () {
          var fichier = uploadPod.files && uploadPod.files[0];
          if (!fichier) { state.pod.file = null; return; }
          var p = state.pod.product || {};
          traiterFichier(fichier, { largeurMm: p.widthMm, hauteurMm: p.heightMm, acceptVector: true })
            .then(function (res) {
              state.pod.file = res;
              if (previewB) afficherApercuFichier(previewB, res, function () { state.pod.file = null; uploadPod.value = ""; if (dpiNoteB) dpiNoteB.hidden = true; });
              afficherNoteDpi(dpiNoteB, res);
              if (previewB) lancerScanVisage(res, previewB);
              if (res.kind === "image" && typeof afficherPaletteExtraite === "function") { afficherPaletteExtraite("pers-palette-extraite-b", res.dataUrl); }
            })
            .catch(function (err) { afficherErreur(err.message); uploadPod.value = ""; });
        });
      }

      var noteB = root.querySelector("#pers-pod-note");
      if (noteB) noteB.addEventListener("input", function () { state.pod.note = noteB.value; });

      var radiosComplexite = root.querySelectorAll('input[name="pers-pod-complexity"]');
      var prixStdSpan = root.querySelector('[data-price-from="pod-service-standard"]');
      var prixAvSpan = root.querySelector('[data-price-from="pod-service-advanced"]');
      if (prixStdSpan) prixStdSpan.textContent = "+" + services.podStandard.priceFormatted;
      if (prixAvSpan) prixAvSpan.textContent = "+" + services.podAdvanced.priceFormatted;
      radiosComplexite.forEach(function (r) { r.addEventListener("change", function () { if (r.checked) state.pod.complexity = r.value; }); });

      var panelB = root.querySelector('[data-flow-panel="pod-image"]');
      if (panelB) {
        var boutonRecapB = panelB.querySelector('[data-step="3b"] .pers-step-next[data-next-step="recap"]');
        if (boutonRecapB) {
          boutonRecapB.addEventListener("click", function () {
            if (!state.pod.file) { afficherErreur(t("error_missing_file")); signalerChampInvalide(uploadPod); return; }
            if (contientContenuSensible()) { buildModeration("verse"); showStep("moderation"); return; }
            if (contientContenuHalalGeneral()) { buildModeration("general"); showStep("moderation"); return; }
            showStep("recap");
          });
        }
      }
    })();

    (function initDigital() {
      var selectC = root.querySelector("#pers-product-select-c");
      var next2c = root.querySelector('.pers-step-next[data-next-step="3c"][data-store-key="productC"]');
      if (next2c) {
        next2c.addEventListener("click", function () {
          if (!selectC || !selectC.value) { afficherErreur(t("error_select_product")); return; }
          var opt = selectC.selectedOptions[0];
          state.digital.product = { variantId: opt.dataset.variantId, title: opt.dataset.title, fullPalette: opt.dataset.fullPalette === "true" };
          var scopeField = root.querySelector("#pers-digital-scope-field");
          var coverFields = root.querySelector("#pers-digital-cover-fields");
          var fullFields = root.querySelector("#pers-digital-full-fields");
          if (scopeField) scopeField.hidden = !state.digital.product.fullPalette;
          if (coverFields) coverFields.hidden = false;
          if (fullFields) fullFields.hidden = true;
          state.digital.scope = "couverture";
          var favPalette = (typeof lirePaletteFav === "function") ? lirePaletteFav() : "";
          if (parametresPartages) {
            if (parametresPartages.font) appliquerValeur("pers-digital-font", parametresPartages.font);
            if (parametresPartages.size) appliquerValeur("pers-digital-size", parametresPartages.size);
            if (parametresPartages.name) appliquerValeur("pers-digital-name", parametresPartages.name);
            if (parametresPartages.dedication) appliquerValeur("pers-digital-dedication", parametresPartages.dedication);
            if (parametresPartages.palette) favPalette = parametresPartages.palette;
          }
          if (favPalette) {
            var favRadio = root.querySelector('.pers-palette-swatch[data-palette="' + favPalette + '"] input[type="radio"]');
            if (favRadio) { favRadio.checked = true; favRadio.dispatchEvent(new Event("change", { bubbles: true })); }
          }
          showStep("3c");
        });
      }

      var radiosScope = root.querySelectorAll('input[name="pers-digital-scope"]');
      radiosScope.forEach(function (r) {
        r.addEventListener("change", function () {
          if (!r.checked) return;
          state.digital.scope = r.value;
          var coverFields = root.querySelector("#pers-digital-cover-fields");
          var fullFields = root.querySelector("#pers-digital-full-fields");
          var estComplete = r.value === "palette-complete";
          if (coverFields) coverFields.hidden = estComplete;
          if (fullFields) fullFields.hidden = !estComplete;
        });
      });

      var paletteRadios = root.querySelectorAll('input[name="pers-palette"]');
      var badgePrix = root.querySelector("#pers-digital-price-badge");
      paletteRadios.forEach(function (r) {
        r.addEventListener("change", function () {
          if (!r.checked) return;
          var label = r.closest(".pers-palette-swatch");
          state.digital.palette = label ? label.dataset.palette : "origine";
          if (badgePrix) {
            if (state.digital.palette === "origine") { badgePrix.hidden = true; }
            else { badgePrix.hidden = false; badgePrix.textContent = "+" + services.coverColor.priceFormatted; }
          }
          majApercuDigital();
        });
      });

      var selFont = root.querySelector("#pers-digital-font");
      var selSize = root.querySelector("#pers-digital-size");
      var champNom = root.querySelector("#pers-digital-name");
      var champDedicace = root.querySelector("#pers-digital-dedication");
      var checkHijri = root.querySelector("#pers-digital-hijri");
      var champNote = root.querySelector("#pers-digital-note");
      var champFullNote = root.querySelector("#pers-digital-full-note");

      var paletteGradients = {
        "origine": "linear-gradient(135deg,#fffdf8,#16233f)", "brique-lin": "linear-gradient(135deg,#78350f,#0f172a)",
        "cuivre-carbone": "linear-gradient(135deg,#0f766e,#18181b)", "sable-iris": "linear-gradient(135deg,#f1d6b8,#3b0764)",
        "dune-argile": "linear-gradient(135deg,#e6ccb2,#582f0e)", "neon-medine": "linear-gradient(135deg,#020617,#38bdf8)",
        "sauge-cristal": "linear-gradient(135deg,#ccd5ae,#283618)", "aura-monolithique": "linear-gradient(135deg,#f8fafc,#020617)",
        "emeraude-or": "linear-gradient(135deg,#0b3d2e,#c9a227)", "levant-mineral": "linear-gradient(135deg,#c17a52,#8a9a5b)",
        "aube-fajr": "linear-gradient(135deg,#fcd5ce,#d8b4e2,#7c6a9c)", "sobriete-accessible": "linear-gradient(135deg,#1e3a5f,#e8a33d)"
      };
      var previewText = root.querySelector("#pers-digital-preview-text");
      function majApercuDigital() {
        if (!previewText) return;
        var contenu = state.digital.dedication.trim() || (champNom && champNom.value.trim()) || t("digital_preview_sample");
        if (checkHijri && checkHijri.checked) {
          var hijri = (typeof gregorienVersHijri === "function") ? gregorienVersHijri(new Date()) : null;
          contenu += hijri ? (" · " + hijri.jour + " " + hijri.mois + " " + hijri.annee + " H") : " · 1448 H";
        }
        previewText.textContent = contenu;
        previewText.style.fontFamily = "'" + state.digital.font + "', Georgia, serif";
        var tailles = { "Compacte": "1rem", "Confortable": "1.3rem", "Grande lisibilité": "1.6rem" };
        previewText.style.fontSize = tailles[state.digital.size] || "1.3rem";
        var preview = root.querySelector("#pers-digital-preview");
        if (preview) preview.style.background = paletteGradients[state.digital.palette] || paletteGradients.origine;
        if (typeof verifierContraste === "function") verifierContraste();
      }

      if (selFont) selFont.addEventListener("change", function () { state.digital.font = selFont.value; majApercuDigital(); });
      if (selSize) selSize.addEventListener("change", function () { state.digital.size = selSize.value; majApercuDigital(); });
      if (champNom) champNom.addEventListener("input", function () { state.digital.name = champNom.value; majApercuDigital(); });
      if (champDedicace) champDedicace.addEventListener("input", function () { state.digital.dedication = champDedicace.value; majApercuDigital(); });
      if (checkHijri) checkHijri.addEventListener("change", function () { state.digital.hijri = checkHijri.checked; majApercuDigital(); });
      if (champNote) champNote.addEventListener("input", function () { state.digital.note = champNote.value; });
      if (champFullNote) champFullNote.addEventListener("input", function () { state.digital.fullNote = champFullNote.value; });
      majApercuDigital();

      var coverFieldsEl = root.querySelector("#pers-digital-cover-fields");
      if (coverFieldsEl) {
        var boutonRecapC = coverFieldsEl.querySelector('.pers-step-next[data-next-step="recap"]');
        if (boutonRecapC) {
          boutonRecapC.addEventListener("click", function () {
            if (state.digital.product && state.digital.product.fullPalette && state.digital.scope !== "couverture") { afficherErreur(t("error_missing_palette")); return; }
            if (contientContenuHalalGeneral()) { buildModeration("general"); showStep("moderation"); return; }
            showStep("recap");
          });
        }
      }

      var boutonFullCta = root.querySelector("#pers-digital-full-cta");
      if (boutonFullCta) {
        boutonFullCta.addEventListener("click", function () {
          if (!champFullNote || !champFullNote.value.trim()) { afficherErreur(t("error_missing_full_note")); return; }
          var lien = root.querySelector("#pers-digital-full-mailto");
          var contact = lireContactOptin("digitalfull");
          if (lien) {
            var corps = "Bonjour,\n\nJe souhaite une palette complète personnalisée pour :\n" + (state.digital.product ? state.digital.product.title : "—") +
              "\n\nDescription souhaitée :\n" + champFullNote.value.trim() + "\n\nCoordonnées de contact :\n" + formaterContact(contact) + "\n\nMerci !";
            lien.href = mailto("Demande de personnalisation — palette digitale complète", corps);
          }
          afficherSucces(t("digital_full_note_sent")); showStep("digital-full-confirm");

          envoyerDevisSiPossible({
            type: "agenda_palette_complete",
            reference: "DEVIS-" + Date.now().toString(36).toUpperCase(),
            article: state.digital.product ? state.digital.product.title : null,
            description: champFullNote.value.trim(),
            contact: contact,
            langue: document.documentElement.getAttribute("lang") || null
          }).then(function (succes) {
            if (succes) {
              var messageConfirm = root.querySelector("#pers-digital-full-confirm-message");
              if (messageConfirm) messageConfirm.textContent = t("digital_full_note_sent_auto");
            }
          });
        });
      }

      var backFull = root.querySelector("#pers-digital-full-back");
      if (backFull) backFull.addEventListener("click", retourAuxChoix);
    })();

    (function initGallery() {
      var selectD = root.querySelector("#pers-product-select-d");
      var next2d = root.querySelector('.pers-step-next[data-next-step="3d"]');
      var galleryProductTags = [];

      if (selectD) {
        selectD.addEventListener("change", function () {
          var opt = selectD.selectedOptions[0];
          if (selectD.value) {
            state.gallery.product = { variantId: selectD.value, productId: opt.dataset.productId, handle: opt.dataset.handle, title: opt.textContent };
            galleryProductTags = (opt.dataset.tags || "").split(",").map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
            if (next2d) next2d.disabled = false;
            applyGalleryFilters();
          } else {
            state.gallery.product = null;
            galleryProductTags = [];
            if (next2d) next2d.disabled = true;
            applyGalleryFilters();
          }
        });
      }
      if (next2d) { next2d.addEventListener("click", function () { if (!state.gallery.product) { afficherErreur(t("error_select_product")); signalerChampInvalide(selectD); return; } showStep("3d"); }); }

      var grille = root.querySelector("#pers-gallery-grid");
      var cartes = grille ? Array.prototype.slice.call(grille.querySelectorAll(".pers-gallery-card")) : [];
      var chips = Array.prototype.slice.call(root.querySelectorAll(".pers-gallery-filter-chip"));
      var searchInput = root.querySelector("#pers-gallery-search");
      var showAllFormatsToggle = root.querySelector("#pers-gallery-show-all-formats");
      var showAllFormatsWrap = root.querySelector("#pers-gallery-formats-toggle-wrap");
      var countEl = root.querySelector("#pers-gallery-count");
      var emptyFilteredEl = root.querySelector("#pers-gallery-empty-filtered");
      var activeCategory = "";
      var hasFormatMismatch = false;

      function cardMatchesFormat(carte) {
        var formats = (carte.dataset.galleryFormats || "").split(",").map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
        if (formats.length === 0) return true;
        if (galleryProductTags.length === 0) return true;
        return formats.some(function (f) { return galleryProductTags.indexOf(f) !== -1; });
      }

      function applyGalleryFilters() {
        var term = searchInput ? searchInput.value.trim().toLowerCase() : "";
        var showAll = showAllFormatsToggle ? showAllFormatsToggle.checked : false;
        var visibleCount = 0;
        hasFormatMismatch = false;

        cartes.forEach(function (carte) {
          var title = (carte.dataset.galleryTitle || "").toLowerCase();
          var category = carte.dataset.galleryCategory || "";
          var matchesCategory = !activeCategory || category === activeCategory;
          var matchesSearch = !term || title.indexOf(term) !== -1 || category.toLowerCase().indexOf(term) !== -1;
          var formatOk = cardMatchesFormat(carte);
          if (!formatOk) hasFormatMismatch = true;

          carte.classList.toggle("is-format-mismatch", !formatOk);

          var visible = matchesCategory && matchesSearch && (formatOk || showAll);
          carte.hidden = !visible;
          if (visible) visibleCount++;
        });

        if (countEl) countEl.textContent = t("gallery_results_count", { count: visibleCount });
        if (emptyFilteredEl) emptyFilteredEl.hidden = visibleCount !== 0;
        if (showAllFormatsWrap) showAllFormatsWrap.hidden = !hasFormatMismatch;
      }

      chips.forEach(function (chip) {
        chip.addEventListener("click", function () {
          chips.forEach(function (c) { c.classList.remove("is-active"); });
          chip.classList.add("is-active");
          activeCategory = chip.dataset.galleryFilter || "";
          applyGalleryFilters();
        });
      });
      if (searchInput) searchInput.addEventListener("input", applyGalleryFilters);
      if (showAllFormatsToggle) showAllFormatsToggle.addEventListener("change", applyGalleryFilters);

      cartes.forEach(function (carte) {
        carte.addEventListener("click", function () {
          cartes.forEach(function (c) { c.classList.remove("is-selected"); c.setAttribute("aria-checked", "false"); });
          carte.classList.add("is-selected"); carte.setAttribute("aria-checked", "true");
          state.gallery.design = {
            id: carte.dataset.galleryBlockId,
            title: carte.dataset.galleryTitle || "",
            image: carte.dataset.galleryImage || "",
            rights: carte.dataset.galleryRights || ""
          };
        });
      });

      applyGalleryFilters();

      var noteD = root.querySelector("#pers-gallery-note");
      if (noteD) noteD.addEventListener("input", function () { state.gallery.note = noteD.value; });

      var ctaD = root.querySelector("#pers-gallery-cta");
      if (ctaD) {
        ctaD.addEventListener("click", function () {
          if (!state.gallery.design) { afficherErreur(t("error_missing_design")); return; }
          if (contientContenuHalalGeneral()) { buildModeration("general"); showStep("moderation"); return; }
          buildGalleryConfirm();
          showStep("gallery-confirm");
        });
      }
    })();

    function buildGalleryConfirm() {
      var lien = root.querySelector("#pers-gallery-mailto");
      var messageEl = root.querySelector("#pers-gallery-confirm-message");
      if (messageEl) messageEl.textContent = t("gallery_confirm_message");
      if (!lien) return;
      var contact = lireContactOptin("gallery");
      var corps = "Bonjour,\n\nJe souhaite ce design de votre galerie sur le support suivant :\n\n" +
        "Support : " + (state.gallery.product ? state.gallery.product.title : "—") +
        "\nDesign choisi : " + (state.gallery.design ? state.gallery.design.title : "—") +
        (state.gallery.note ? ("\n\nPrécisions :\n" + state.gallery.note) : "") +
        "\n\nCoordonnées de contact :\n" + formaterContact(contact) + "\n\nMerci !";
      lien.href = mailto(t("gallery_mailto_subject"), corps);

      envoyerDevisSiPossible({
        type: "design_galerie",
        reference: "DEVIS-" + Date.now().toString(36).toUpperCase(),
        support: state.gallery.product ? state.gallery.product.title : null,
        design_id: state.gallery.design ? state.gallery.design.id : null,
        design_titre: state.gallery.design ? state.gallery.design.title : null,
        design_visuel_url: state.gallery.design ? state.gallery.design.image : null,
        precisions: state.gallery.note || "",
        contact: contact,
        langue: document.documentElement.getAttribute("lang") || null
      }).then(function (succes) {
        if (succes && messageEl) { messageEl.textContent = t("digital_full_note_sent_auto"); }
      });
    }

    var backGallery = root.querySelector("#pers-gallery-back");
    if (backGallery) backGallery.addEventListener("click", retourAuxChoix);

    function buildModeration(raison) {
      var heading = root.querySelector("#pers-moderation-heading");
      var message = root.querySelector("#pers-moderation-message");
      var lien = root.querySelector("#pers-moderation-contact");
      if (heading) heading.textContent = t("moderation_heading");
      if (message) {
        if (raison === "verse") message.textContent = t("moderation_message_verse");
        else if (raison === "doubt") message.textContent = t("moderation_message_doubt");
        else message.textContent = t("moderation_message_general");
      }
      if (lien) {
        var contact = lireContactOptin("moderation");
        var corps = "Bonjour,\n\nMa demande de personnalisation nécessite une vérification :\n\nParcours : " + (state.flow || "—") + "\n\nCoordonnées de contact :\n" + formaterContact(contact) + "\n\nMerci !";
        lien.href = mailto(t("moderation_mailto_subject"), corps);
      }
    }

    var backModeration = root.querySelector("#pers-moderation-back");
    if (backModeration) backModeration.addEventListener("click", retourAuxChoix);
    var backEchanges = root.querySelector("#pers-echanges-back");
    if (backEchanges) backEchanges.addEventListener("click", retourAuxChoix);

    var noteOos = root.querySelector("#pers-oos-note");
    var lienOos = root.querySelector("#pers-oos-contact");
    if (lienOos) {
      lienOos.addEventListener("click", function (e) {
        if (!noteOos || !noteOos.value.trim()) { e.preventDefault(); afficherErreur(t("error_missing_scope_note")); return; }
        var contact = lireContactOptin("oos");
        var corps = "Bonjour,\n\nVoici ma demande hors périmètre :\n\n" + noteOos.value.trim() + "\n\nCoordonnées de contact :\n" + formaterContact(contact) + "\n\nMerci !";
        lienOos.href = mailto("Demande de personnalisation hors périmètre", corps);

        envoyerDevisSiPossible({
          type: "hors_perimetre",
          reference: "DEVIS-" + Date.now().toString(36).toUpperCase(),
          description: noteOos.value.trim(),
          contact: contact,
          langue: document.documentElement.getAttribute("lang") || null
        }).then(function (succes) {
          if (succes) {
            var texteOos = root.querySelector("#pers-oos-message-text");
            if (texteOos) texteOos.textContent = t("oos_note_sent_auto");
          }
        });
      });
    }
    var backOos = root.querySelector("#pers-oos-back");
    if (backOos) { backOos.addEventListener("click", function () { if (el.oosTrigger) el.oosTrigger.setAttribute("aria-expanded", "false"); retourAuxChoix(); }); }

    /**
     * Mini-aperçu visuel dans le récapitulatif (v25).
     * Purement additif à l'affichage : n'influence jamais le panier, le
     * prix ou les propriétés de commande.
     *  - étiquette-cdv : photo produit + texte recto en incrustation
     *    (+ miniature du logo si un logo a été envoyé).
     *  - pod-image : aperçu du visuel réellement envoyé par la cliente,
     *    posé sur la photo produit en petite vignette.
     *  - digital-style : réutilise le dégradé de palette + la police
     *    choisie, avec le texte de dédicace/nom déjà saisi.
     *  - gallery-design : n'a pas de récap (parcours devis uniquement,
     *    jamais de panier) — rien à faire ici pour ce flow.
     */
    function buildRecapVisual() {
      if (!el.recapVisual) return;
      el.recapVisual.innerHTML = ""; el.recapVisual.className = "pers-recap-visual";

      function placeholder(texte) {
        el.recapVisual.innerHTML =
          '<div class="pers-recap-visual__placeholder">' +
          '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"></rect><circle cx="8.5" cy="9.5" r="1.5"></circle><path d="M21 15l-5-5-9 9"></path></svg>' +
          '<span>' + texte + '</span></div>';
      }

      if (state.flow === "etiquette-cdv") {
        var e3 = state.etiquette;
        var imgLogo = e3.logo && e3.logo.kind === "image" ? e3.logo.dataUrl : null;
        var imgProduit = e3.product && e3.product.image ? e3.product.image : null;
        if (!imgLogo && !imgProduit) { placeholder(t("recap_visual_placeholder") || "Aperçu visuel indisponible pour cet article."); return; }
        el.recapVisual.classList.add("pers-recap-visual--card");
        var texteRecto = e3.lines.filter(Boolean).join(" · ");
        var mediaHtml = '<div class="pers-recap-visual__media"' + (imgProduit && !imgLogo ? ' style="background-image:url(\'' + imgProduit.replace(/'/g, "%27") + '\')"' : '') + '>';
        if (imgLogo) { mediaHtml += '<img src="' + imgLogo + '" alt="' + (t("apercu_alt") || "") + '">'; }
        if (imgProduit && imgLogo) { mediaHtml += '<img class="pers-recap-visual__product-thumb" src="' + imgProduit + '" alt="">'; }
        if (texteRecto || (e3.product && e3.product.title)) {
          mediaHtml += '<div class="pers-recap-visual__overlay-text">' + (e3.product && e3.product.title ? '<strong>' + escapeHtml(e3.product.title) + '</strong>' : '') + (texteRecto ? escapeHtml(texteRecto) : '') + '</div>';
        }
        mediaHtml += '</div>';
        el.recapVisual.innerHTML = mediaHtml;
        return;
      }

      if (state.flow === "pod-image") {
        var p3 = state.pod;
        if (!p3.file || p3.file.kind !== "image") { placeholder(t("recap_visual_placeholder_pod") || "Fichier envoyé — vérification manuelle avant impression."); return; }
        el.recapVisual.classList.add("pers-recap-visual--card");
        var mediaHtmlB = '<div class="pers-recap-visual__media">';
        mediaHtmlB += '<img src="' + p3.file.dataUrl + '" alt="' + (t("apercu_alt") || "") + '">';
        if (p3.product && p3.product.image) { mediaHtmlB += '<img class="pers-recap-visual__product-thumb" src="' + p3.product.image + '" alt="">'; }
        mediaHtmlB += '</div>';
        if (p3.product && p3.product.title) { mediaHtmlB += '<p class="pers-recap-visual__caption">' + escapeHtml(p3.product.title) + '</p>'; }
        el.recapVisual.innerHTML = mediaHtmlB;
        return;
      }

      if (state.flow === "digital-style") {
        var d3 = state.digital;
        var paletteGradientsRecap = {
          "origine": "linear-gradient(135deg,#fffdf8,#16233f)", "brique-lin": "linear-gradient(135deg,#78350f,#0f172a)",
          "cuivre-carbone": "linear-gradient(135deg,#0f766e,#18181b)", "sable-iris": "linear-gradient(135deg,#f1d6b8,#3b0764)",
          "dune-argile": "linear-gradient(135deg,#e6ccb2,#582f0e)", "neon-medine": "linear-gradient(135deg,#020617,#38bdf8)",
          "sauge-cristal": "linear-gradient(135deg,#ccd5ae,#283618)", "aura-monolithique": "linear-gradient(135deg,#f8fafc,#020617)",
          "emeraude-or": "linear-gradient(135deg,#0b3d2e,#c9a227)", "levant-mineral": "linear-gradient(135deg,#c17a52,#8a9a5b)",
          "aube-fajr": "linear-gradient(135deg,#fcd5ce,#d8b4e2,#7c6a9c)", "sobriete-accessible": "linear-gradient(135deg,#1e3a5f,#e8a33d)"
        };
        el.recapVisual.classList.add("pers-recap-visual--digital");
        var contenuTexte = d3.dedication.trim() || d3.name.trim() || (d3.product ? d3.product.title : "") || t("digital_preview_sample");
        var gradient = paletteGradientsRecap[d3.palette] || paletteGradientsRecap.origine;
        el.recapVisual.innerHTML =
          '<div class="pers-recap-visual__media" style="background:' + gradient + '">' +
          '<span class="pers-recap-visual__digital-text" style="font-family:\'' + d3.font.replace(/'/g, "") + '\', Georgia, serif;">' + escapeHtml(contenuTexte) + '</span>' +
          '</div>';
        return;
      }

      el.recapVisual.hidden = true;
    }

    function buildRecap() {
      if (!el.recapContent) return;
      if (el.recapVisual) el.recapVisual.hidden = false;
      buildRecapVisual();
      var lignes = []; var total = 0; var contientImage = false;

      if (state.flow === "etiquette-cdv") {
        var e2 = state.etiquette;
        contientImage = !!(e2.logo && e2.logo.kind === "image") || !!(e2.versoLogo && e2.versoLogo.kind === "image");
        lignes.push(['<strong>' + t("recap_product_label") + ' :</strong> ' + (e2.product ? e2.product.title : "—")]);
        lignes.push(['<strong>Recto :</strong> ' + (e2.lines.filter(Boolean).map(function (l) { return escapeHtml(l); }).join(" — ") || "—")]);
        if (e2.logo) lignes.push(['Logo recto : ' + (e2.logo.file ? escapeHtml(e2.logo.file.name) : "—")]);
        if (e2.hasVerso) {
          lignes.push(['<strong>Verso :</strong> ' + (e2.versoLines.filter(Boolean).map(function (l) { return escapeHtml(l); }).join(" — ") || "—")]);
          if (e2.versoLogo) lignes.push(['Logo verso : ' + (e2.versoLogo.file ? escapeHtml(e2.versoLogo.file.name) : "—")]);
        }
        if (e2.service === "impression_envoi") { lignes.push([t("service_print_ship") + ' <strong>+' + services.print.priceFormatted + '</strong>']); total += services.print.priceCents; }
        else { lignes.push([t("service_file_only") + ' — ' + t("included")]); }
        if (e2.formatAdapt) {
          if (e2.service === "impression_envoi") { lignes.push([t("format_adapt_label") + ' — ' + t("included")]); }
          else { lignes.push([t("format_adapt_label") + ' <strong>+' + services.formatAdapt.priceFormatted + '</strong>']); total += services.formatAdapt.priceCents; }
        }
        if (el.confirmAccuracyLabel) el.confirmAccuracyLabel.textContent = t("recap_confirm_etiquette");
      }

      if (state.flow === "pod-image") {
        var p2 = state.pod;
        contientImage = !!(p2.file && p2.file.kind === "image");
        lignes.push(['<strong>' + t("recap_product_label") + ' :</strong> ' + (p2.product ? p2.product.title : "—")]);
        lignes.push(['Fichier : ' + (p2.file && p2.file.file ? escapeHtml(p2.file.file.name) : "—")]);
        if (p2.note) lignes.push([escapeHtml(p2.note)]);
        var serviceLabel = p2.complexity === "avance" ? t("pod_advanced") : t("pod_standard");
        var serviceObj = p2.complexity === "avance" ? services.podAdvanced : services.podStandard;
        lignes.push([serviceLabel + ' <strong>+' + serviceObj.priceFormatted + '</strong>']); total += serviceObj.priceCents;
        if (el.confirmAccuracyLabel) el.confirmAccuracyLabel.textContent = t("recap_confirm_pod");
      }

      if (state.flow === "digital-style") {
        var d2 = state.digital;
        lignes.push(['<strong>' + t("recap_product_label") + ' :</strong> ' + (d2.product ? d2.product.title : "—")]);
        var stylePieces = [d2.font, d2.size];
        if (d2.name) stylePieces.push(d2.name);
        if (d2.dedication) stylePieces.push('« ' + escapeHtml(d2.dedication) + ' »');
        if (d2.hijri) stylePieces.push("+ millésime hégirien");
        lignes.push(['<span class="pers-recap-style">' + stylePieces.map(escapeHtml).join(" · ") + '</span>']);
        if (d2.palette !== "origine") { lignes.push(['Couleur de couverture <strong>+' + services.coverColor.priceFormatted + '</strong>']); total += services.coverColor.priceCents; }
        else { lignes.push(['Couleur de couverture — ' + t("included")]); }
        if (el.confirmAccuracyLabel) el.confirmAccuracyLabel.textContent = t("confirm_agenda_cover");
      }

      el.recapContent.innerHTML = lignes.map(function (l) { return "<p>" + l.join("") + "</p>"; }).join("");

      if (navigator.clipboard && !root.querySelector("#pers-recap-copy")) {
        var boutonCopier = document.createElement("button");
        boutonCopier.type = "button"; boutonCopier.id = "pers-recap-copy"; boutonCopier.className = "pers-btn-link";
        boutonCopier.textContent = t("copy_recap") || "Copier ce récapitulatif";
        boutonCopier.addEventListener("click", function () {
          var texteBrut = el.recapContent.textContent.replace(/\s+/g, " ").trim();
          navigator.clipboard.writeText(texteBrut).then(function () {
            boutonCopier.textContent = t("copied") || "Copié ✓";
            setTimeout(function () { boutonCopier.textContent = t("copy_recap") || "Copier ce récapitulatif"; }, 2000);
          }).catch(function () { });
        });
        el.recapContent.parentNode.insertBefore(boutonCopier, el.recapContent.nextSibling);
      }

      if (el.priceTotal) el.priceTotal.textContent = formaterPrix(total);
      if (el.confirmAccuracy) el.confirmAccuracy.checked = false;
      if (el.halalConfirm) el.halalConfirm.checked = false;
      if (el.halalDetails) el.halalDetails.hidden = true;
      if (el.halalDetailsToggle) el.halalDetailsToggle.setAttribute("aria-expanded", "false");

      var giftCb = root.querySelector("#pers-gift-mode");
      var giftWrap = root.querySelector("#pers-gift-message-wrap");
      var giftMsg = root.querySelector("#pers-gift-message");
      if (giftCb) giftCb.checked = false; if (giftWrap) giftWrap.hidden = true; if (giftMsg) giftMsg.value = "";
      if (typeof majUpsellKit === "function") majUpsellKit();
      state.recapContientImage = contientImage;
      miseAJourBoutonAjout();
    }

    function escapeHtml(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }

    function miseAJourBoutonAjout() {
      if (!el.addToCartBtn) return;
      var accuracyOk = !!(el.confirmAccuracy && el.confirmAccuracy.checked);
      var halalOk = !!(el.halalConfirm && el.halalConfirm.checked);
      el.addToCartBtn.disabled = state.locked || !(accuracyOk && halalOk);
    }

    if (el.confirmAccuracy) { el.confirmAccuracy.addEventListener("change", miseAJourBoutonAjout); }
    if (el.halalConfirm) { el.halalConfirm.addEventListener("change", miseAJourBoutonAjout); }
    if (el.halalDetailsToggle && el.halalDetails) {
      el.halalDetailsToggle.addEventListener("click", function () {
        var ouvert = !el.halalDetails.hidden; el.halalDetails.hidden = ouvert; el.halalDetailsToggle.setAttribute("aria-expanded", String(!ouvert));
      });
    }
    if (el.halalDoubtBtn) { el.halalDoubtBtn.addEventListener("click", function () { buildModeration("doubt"); showStep("moderation"); }); }

    function envoyerVisuelSiPossible(fichierState, referenceCommande) {
      if (!fichierState || fichierState.kind !== "image" || !makeWebhookUrl) return Promise.resolve(null);
      var donnees = new FormData(); donnees.append("fichier", fichierState.file); donnees.append("reference", referenceCommande);
      var controleur = (typeof AbortController !== "undefined") ? new AbortController() : null;
      var minuteur = controleur ? setTimeout(function () { controleur.abort(); }, 10000) : null;
      return fetch(makeWebhookUrl, { method: "POST", body: donnees, signal: controleur ? controleur.signal : undefined })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (json) { return json && json.url ? json.url : null; })
        .catch(function () { return null; })
        .finally(function () { if (minuteur) clearTimeout(minuteur); });
    }

    function construireProprietes(referenceCommande) {
      var props = { "Référence personnalisation": referenceCommande };
      if (state.flow === "etiquette-cdv") {
        var e = state.etiquette;
        props["Parcours"] = "Étiquette / carte de visite";
        e.lines.forEach(function (l, i) { if (l && l.trim()) props["Texte recto ligne " + (i + 1)] = l.trim(); });
        if (e.logo) {
          props["Logo recto"] = e.logo.file ? e.logo.file.name : "—";
          if (e.logo.dpi) props["DPI logo recto"] = String(e.logo.dpi);
          props["Placement logo recto (x,y,échelle,rotation)"] = Math.round(e.logo.transform.x) + "," + Math.round(e.logo.transform.y) + "," + e.logo.transform.scale.toFixed(2) + "," + e.logo.transform.rotation + "°";
          if (e.logo.faceScan === "oui") props["Scan visage recto (auto)"] = "détecté — à vérifier";
        }
        if (e.hasVerso) {
          e.versoLines.forEach(function (l, i) { if (l && l.trim()) props["Texte verso ligne " + (i + 1)] = l.trim(); });
          if (e.versoLogo) {
            props["Logo verso"] = e.versoLogo.file ? e.versoLogo.file.name : "—";
            if (e.versoLogo.dpi) props["DPI logo verso"] = String(e.versoLogo.dpi);
            if (e.versoLogo.faceScan === "oui") props["Scan visage verso (auto)"] = "détecté — à vérifier";
          }
        }
        props["Service"] = e.service === "impression_envoi" ? "Impression et envoi" : "Fichier de calage uniquement";
        if (e.formatAdapt) props["Adaptation de format"] = "oui";
      }
      if (state.flow === "pod-image") {
        var p = state.pod;
        props["Parcours"] = "Visuel personnel (POD)";
        props["Fichier"] = p.file && p.file.file ? p.file.file.name : "—";
        if (p.file && p.file.dpi) props["DPI"] = String(p.file.dpi);
        if (p.file && p.file.faceScan === "oui") props["Scan visage (auto)"] = "détecté — à vérifier";
        if (p.file) { props["Placement (x,y,échelle,rotation)"] = Math.round(p.file.transform.x) + "," + Math.round(p.file.transform.y) + "," + p.file.transform.scale.toFixed(2) + "," + p.file.transform.rotation + "°"; }
        if (p.note) props["Note pour l'équipe"] = p.note;
        props["Niveau de calage"] = p.complexity === "avance" ? "Avancé" : "Standard";
      }
      if (state.flow === "digital-style") {
        var d = state.digital;
        props["Parcours"] = "Style visuel digital"; props["Police"] = d.font; props["Taille de texte"] = d.size;
        if (d.name) props["Nom"] = d.name;
        if (d.dedication) props["Dédicace"] = d.dedication;
        if (d.hijri) props["Millésime hégirien"] = "oui";
        props["Couleur de couverture"] = d.palette;
        if (d.note) props["Note pour l'équipe"] = d.note;
      }
      if (state.recapContientImage) { props["Confirmation absence visage"] = el.halalConfirm && el.halalConfirm.checked ? "oui" : "non"; }
      var giftCbProp = root.querySelector("#pers-gift-mode");
      if (giftCbProp && giftCbProp.checked) {
        props["Cadeau"] = "oui"; props["Masquer le prix sur le bon de livraison"] = "oui";
        var giftMsgProp = root.querySelector("#pers-gift-message");
        if (giftMsgProp && giftMsgProp.value.trim()) props["Message cadeau"] = giftMsgProp.value.trim();
      }
      return props;
    }

    function ajouterAuPanier() {
      masquerErreur(); el.addToCartBtn.disabled = true; el.addToCartBtn.textContent = t("adding");
      var referenceCommande = "PERS-" + Date.now().toString(36).toUpperCase();
      var fichierPourEnvoi = null;
      if (state.flow === "etiquette-cdv" && state.etiquette.logo) fichierPourEnvoi = state.etiquette.logo;
      if (state.flow === "pod-image" && state.pod.file) fichierPourEnvoi = state.pod.file;

      envoyerVisuelSiPossible(fichierPourEnvoi, referenceCommande).then(function (urlFichier) {
        var proprietes = construireProprietes(referenceCommande);
        if (urlFichier) proprietes["Fichier haute résolution (URL)"] = urlFichier;

        var items = [];
        if (state.flow === "etiquette-cdv" && state.etiquette.product) {
          items.push({ id: state.etiquette.product.variantId, quantity: 1, properties: proprietes });
          if (state.etiquette.service === "impression_envoi" && services.print.variantId) { items.push({ id: services.print.variantId, quantity: 1, properties: { "Référence personnalisation": referenceCommande } }); }
          if (state.etiquette.formatAdapt && state.etiquette.service !== "impression_envoi" && services.formatAdapt.variantId) { items.push({ id: services.formatAdapt.variantId, quantity: 1, properties: { "Référence personnalisation": referenceCommande } }); }
        } else if (state.flow === "pod-image" && state.pod.product) {
          items.push({ id: state.pod.product.variantId, quantity: 1, properties: proprietes });
          var serviceVariant = state.pod.complexity === "avance" ? services.podAdvanced.variantId : services.podStandard.variantId;
          if (serviceVariant) items.push({ id: serviceVariant, quantity: 1, properties: { "Référence personnalisation": referenceCommande } });
        } else if (state.flow === "digital-style" && state.digital.product) {
          items.push({ id: state.digital.product.variantId, quantity: 1, properties: proprietes });
          if (state.digital.palette !== "origine" && services.coverColor.variantId) { items.push({ id: services.coverColor.variantId, quantity: 1, properties: { "Référence personnalisation": referenceCommande } }); }
        }

        if (!items.length || !items[0].id) { afficherErreur(t("error_add_to_cart")); el.addToCartBtn.disabled = false; el.addToCartBtn.textContent = t("recap_cta"); return; }

        var racineRoutes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        fetch(racineRoutes + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify({ items: items }) })
          .then(function (res) { if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || "Erreur panier"); }); return res.json(); })
          .then(function () {
            verrouillerFormulaire(true);
            document.dispatchEvent(new CustomEvent("cart:refresh"));
            document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
            var snapshotHistorique = JSON.parse(JSON.stringify(state, function (k, v) { if (k === "file" || k === "dataUrl") return undefined; return v; }));
            ajouterAHistorique(snapshotHistorique); afficherHistorique();
            afficherSucces(
              '<p>' + t("added_to_cart") + '</p>' + '<div class="pers-recap-post-actions">' +
              '<a class="pers-btn pers-btn--primary" href="/cart">' + t("view_cart") + '</a>' +
              '<button type="button" class="pers-btn pers-btn--secondary" id="pers-start-new">' + t("start_new") + '</button>' +
              '<button type="button" class="pers-btn-link" id="pers-duplicate">' + (t("duplicate_cta") || "Dupliquer pour un autre article") + '</button>' + '</div>'
            );
            var boutonNouveau = document.getElementById("pers-start-new");
            if (boutonNouveau) boutonNouveau.addEventListener("click", function () { effacerBrouillon(); window.location.reload(); });
            var boutonDupliquer = document.getElementById("pers-duplicate");
            if (boutonDupliquer) boutonDupliquer.addEventListener("click", function () { verrouillerFormulaire(false); state.history = ["1"]; restaurerDepuisBrouillon(snapshotHistorique); });
          })
          .catch(function (err) { afficherErreur(t("error_add_to_cart") + " (" + err.message + ")"); })
          .finally(function () { el.addToCartBtn.textContent = t("recap_cta"); if (!state.locked) miseAJourBoutonAjout(); });
      });
    }

    function verrouillerFormulaire(vrai) {
      state.locked = vrai; root.classList.toggle("is-locked", vrai);
      if (el.addToCartBtn) el.addToCartBtn.disabled = true;
      if (!vrai) { if (el.successBanner) el.successBanner.hidden = true; }
    }

    if (el.addToCartBtn) {
      el.addToCartBtn.addEventListener("click", function () {
        if (!el.confirmAccuracy || !el.confirmAccuracy.checked) return;
        if (!el.halalConfirm || !el.halalConfirm.checked) return;
        ajouterAuPanier();
      });
    }

    window.addEventListener("beforeunload", function (e) {
      if (state.locked) return;
      var saisieEnCours = state.currentStep !== "1" && (
        state.etiquette.lines.some(Boolean) || !!state.etiquette.logo || state.etiquette.versoLines.some(Boolean) || !!state.etiquette.versoLogo ||
        !!state.pod.file || !!state.pod.note || !!state.digital.name || !!state.digital.dedication || !!state.digital.note
      );
      if (saisieEnCours) { e.preventDefault(); e.returnValue = ""; return ""; }
    });

    var cleBrouillon = "pers_module_brouillon_" + (root.id || "default");
    function sauvegarderBrouillon() {
      try {
        var copie = JSON.parse(JSON.stringify(state, function (k, v) { if (k === "file" || k === "dataUrl") return undefined; return v; }));
        localStorage.setItem(cleBrouillon, JSON.stringify({ savedAt: Date.now(), state: copie }));
      } catch (e) { }
    }
    function effacerBrouillon() { try { localStorage.removeItem(cleBrouillon); } catch (e) { } }
    function appliquerValeur(id, valeur) {
      var champ = root.querySelector("#" + id);
      if (!champ || valeur == null || valeur === "") return;
      var estCaseOuListe = champ.type === "checkbox" || champ.tagName === "SELECT";
      if (champ.type === "checkbox") { champ.checked = !!valeur; } else { champ.value = valeur; }
      champ.dispatchEvent(new Event(estCaseOuListe ? "change" : "input", { bubbles: true }));
    }
    function cocherRadio(name, valeur) {
      if (!valeur) return;
      var radio = root.querySelector('input[name="' + name + '"][value="' + valeur + '"]');
      if (radio) { radio.checked = true; radio.dispatchEvent(new Event("change", { bubbles: true })); }
    }

    function restaurerDepuisBrouillon(s) {
      state.flow = s.flow;
      el.cards.forEach(function (c) { c.setAttribute("aria-pressed", c.dataset.flow === s.flow ? "true" : "false"); });
      if (s.flow === "etiquette-cdv" && s.etiquette) {
        if (s.etiquette.product && s.etiquette.product.variantId) appliquerValeur("pers-product-select-a", s.etiquette.product.variantId);
        appliquerValeur("pers-text-1", s.etiquette.lines && s.etiquette.lines[0]);
        appliquerValeur("pers-text-2", s.etiquette.lines && s.etiquette.lines[1]);
        appliquerValeur("pers-text-3", s.etiquette.lines && s.etiquette.lines[2]);
        if (s.etiquette.versoLines) {
          appliquerValeur("pers-text-verso-1", s.etiquette.versoLines[0]);
          appliquerValeur("pers-text-verso-2", s.etiquette.versoLines[1]);
          appliquerValeur("pers-text-verso-3", s.etiquette.versoLines[2]);
        }
        cocherRadio("pers-service-a", s.etiquette.service);
        appliquerValeur("pers-format-adapt", s.etiquette.formatAdapt);
        appliquerValeur("pers-etiquette-echanges", s.etiquette.echanges);
        showStep(s.etiquette.product ? "3a" : "2a", { viaBack: true });
      } else if (s.flow === "pod-image" && s.pod) {
        if (s.pod.product && s.pod.product.variantId) appliquerValeur("pers-product-select-b", s.pod.product.variantId);
        appliquerValeur("pers-pod-note", s.pod.note);
        cocherRadio("pers-pod-complexity", s.pod.complexity);
        showStep("3b", { viaBack: true });
      } else if (s.flow === "digital-style" && s.digital) {
        appliquerValeur("pers-digital-font", s.digital.font);
        appliquerValeur("pers-digital-size", s.digital.size);
        appliquerValeur("pers-digital-name", s.digital.name);
        appliquerValeur("pers-digital-dedication", s.digital.dedication);
        appliquerValeur("pers-digital-hijri", s.digital.hijri);
        appliquerValeur("pers-digital-note", s.digital.note);
        showStep("2c", { viaBack: true });
      } else if (s.flow === "gallery-design" && s.gallery) {
        if (s.gallery.product && s.gallery.product.variantId) appliquerValeur("pers-product-select-d", s.gallery.product.variantId);
        appliquerValeur("pers-gallery-note", s.gallery.note);
        showStep(s.gallery.product ? "3d" : "2d", { viaBack: true });
        if (s.gallery.design && s.gallery.design.id) {
          var carteRestauree = root.querySelector('.pers-gallery-card[data-gallery-block-id="' + s.gallery.design.id + '"]');
          if (carteRestauree) { carteRestauree.click(); }
        }
      }
      afficherSucces(t("draft_restored") || "Votre brouillon a bien été restauré.");
    }

    (function proposerRestaurationBrouillon() {
      try {
        var brut = localStorage.getItem(cleBrouillon);
        if (!brut) return;
        var paquet = JSON.parse(brut);
        if (!paquet || (Date.now() - paquet.savedAt) > DRAFT_TTL_MS || !paquet.state || !paquet.state.flow) { effacerBrouillon(); return; }
        var conteneur = el.step1 && el.step1.querySelector(".pers-module__cards");
        if (!conteneur) return;
        var banniere = document.createElement("div"); banniere.className = "pers-draft-banner"; banniere.setAttribute("role", "status");
        banniere.innerHTML =
          '<p>' + (t("draft_found") || "Vous avez commencé une personnalisation récemment.") + '</p>' +
          '<div class="pers-draft-banner__actions">' +
          '<button type="button" class="pers-btn pers-btn--secondary" data-action="resume">' + (t("draft_resume") || "Reprendre où j'en étais") + '</button>' +
          '<button type="button" class="pers-btn-link" data-action="discard">' + (t("draft_discard") || "Recommencer à zéro") + '</button>' + '</div>';
        conteneur.parentNode.insertBefore(banniere, conteneur);
        banniere.querySelector('[data-action="resume"]').addEventListener("click", function () { banniere.remove(); restaurerDepuisBrouillon(paquet.state); });
        banniere.querySelector('[data-action="discard"]').addEventListener("click", function () { effacerBrouillon(); banniere.remove(); });
      } catch (e) { }
    })();

    var cleHistorique = "pers_module_historique_" + (root.id || "default");
    function lireHistorique() { try { var brut = localStorage.getItem(cleHistorique); return brut ? JSON.parse(brut) : []; } catch (e) { return []; } }
    function ajouterAHistorique(entree) {
      try { var liste = lireHistorique(); liste.unshift(entree); liste = liste.slice(0, 5); localStorage.setItem(cleHistorique, JSON.stringify(liste)); } catch (e) { }
    }
    function resumeEntreeHistorique(s) {
      if (s.flow === "etiquette-cdv" && s.etiquette) return (s.etiquette.lines || []).filter(Boolean).join(" — ") || t("recap_product_label");
      if (s.flow === "pod-image" && s.pod) return s.pod.note || t("recap_product_label");
      if (s.flow === "digital-style" && s.digital) return s.digital.dedication || s.digital.name || t("recap_product_label");
      if (s.flow === "gallery-design" && s.gallery) return (s.gallery.design && s.gallery.design.title) || t("recap_product_label");
      return t("recap_product_label");
    }
    function afficherHistorique() {
      var wrap = root.querySelector("[data-pers-history-wrap]");
      var conteneur = root.querySelector("#pers-history-list");
      if (!wrap || !conteneur) return;
      var liste = lireHistorique();
      if (!liste.length) { wrap.hidden = true; return; }
      wrap.hidden = false; conteneur.innerHTML = "";
      liste.forEach(function (entree) {
        var item = document.createElement("li"); item.className = "pers-history-item";
        var bouton = document.createElement("button"); bouton.type = "button"; bouton.className = "pers-btn-link";
        bouton.textContent = escapeHtml(resumeEntreeHistorique(entree));
        bouton.addEventListener("click", function () { state.history = ["1"]; restaurerDepuisBrouillon(entree); });
        item.appendChild(bouton); conteneur.appendChild(item);
      });
    }
    afficherHistorique();

    var giftCheckbox = root.querySelector("#pers-gift-mode");
    var giftMessageWrap = root.querySelector("#pers-gift-message-wrap");
    var giftMessage = root.querySelector("#pers-gift-message");
    if (giftCheckbox) { giftCheckbox.addEventListener("change", function () { if (giftMessageWrap) giftMessageWrap.hidden = !giftCheckbox.checked; }); }

    var clePaletteFav = "pers_module_palette_favorite";
    function lirePaletteFav() { try { return localStorage.getItem(clePaletteFav) || ""; } catch (e) { return ""; } }
    function ecrirePaletteFav(val) { try { localStorage.setItem(clePaletteFav, val); } catch (e) { } }
    root.querySelectorAll("[data-fav-palette]").forEach(function (btn) {
      var cle = btn.dataset.favPalette;
      if (lirePaletteFav() === cle) btn.classList.add("is-favorite");
      btn.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation();
        var nouveau = lirePaletteFav() === cle ? "" : cle;
        ecrirePaletteFav(nouveau);
        root.querySelectorAll("[data-fav-palette]").forEach(function (b) { b.classList.toggle("is-favorite", b.dataset.favPalette === nouveau); });
      });
    });

    var boutonPartage = root.querySelector("#pers-digital-share");
    if (boutonPartage) {
      boutonPartage.addEventListener("click", function () {
        var params = new URLSearchParams(window.location.search);
        params.set("pers_font", state.digital.font); params.set("pers_size", state.digital.size); params.set("pers_palette", state.digital.palette);
        if (state.digital.name) params.set("pers_name", state.digital.name);
        if (state.digital.dedication) params.set("pers_dedication", state.digital.dedication);
        var url = window.location.origin + window.location.pathname + "?" + params.toString();
        var texteBouton = boutonPartage.textContent;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () { boutonPartage.textContent = t("link_copied") || "Lien copié ✓"; setTimeout(function () { boutonPartage.textContent = texteBouton; }, 2000); }).catch(function () { });
        }
      });
    }
    var parametresPartages = (function () {
      var params = new URLSearchParams(window.location.search);
      if (!params.has("pers_font") && !params.has("pers_dedication") && !params.has("pers_palette")) return null;
      return { font: params.get("pers_font"), size: params.get("pers_size"), palette: params.get("pers_palette"), name: params.get("pers_name"), dedication: params.get("pers_dedication") };
    })();

    var paletteCouleurs = {
      "origine": ["#fffdf8", "#16233f"], "brique-lin": ["#78350f", "#0f172a"], "cuivre-carbone": ["#0f766e", "#18181b"], "sable-iris": ["#f1d6b8", "#3b0764"],
      "dune-argile": ["#e6ccb2", "#582f0e"], "neon-medine": ["#020617", "#38bdf8"], "sauge-cristal": ["#ccd5ae", "#283618"], "aura-monolithique": ["#f8fafc", "#020617"],
      "emeraude-or": ["#0b3d2e", "#c9a227"], "levant-mineral": ["#c17a52", "#8a9a5b"], "aube-fajr": ["#fcd5ce", "#7c6a9c"], "sobriete-accessible": ["#1e3a5f", "#e8a33d"]
    };
    function luminanceRelative(hex) {
      var c = hex.replace("#", "");
      var r = parseInt(c.substr(0, 2), 16) / 255, g = parseInt(c.substr(2, 2), 16) / 255, b = parseInt(c.substr(4, 2), 16) / 255;
      var lin = [r, g, b].map(function (v) { return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
      return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
    }
    function ratioContraste(hex1, hex2) {
      var l1 = luminanceRelative(hex1), l2 = luminanceRelative(hex2);
      var clair = Math.max(l1, l2), sombre = Math.min(l1, l2);
      return (clair + 0.05) / (sombre + 0.05);
    }
    function verifierContraste() {
      var noteContraste = root.querySelector("#pers-contrast-note");
      if (!noteContraste) return;
      var couple = paletteCouleurs[state.digital.palette];
      if (!couple) { noteContraste.hidden = true; return; }
      var ratio = ratioContraste(couple[0], couple[1]);
      if (ratio < 4.5) { noteContraste.hidden = false; noteContraste.textContent = t("contrast_warning") || "Contraste faible entre le texte et le fond pour cette combinaison — la lisibilité peut être réduite à l'impression comme à l'écran."; }
      else { noteContraste.hidden = true; }
    }

    function extraireCouleursDominantes(dataUrl, callback) {
      var img = new Image();
      img.onload = function () {
        var taille = 40;
        var canvas = document.createElement("canvas"); canvas.width = taille; canvas.height = taille;
        var ctx = canvas.getContext("2d"); ctx.drawImage(img, 0, 0, taille, taille);
        var data;
        try { data = ctx.getImageData(0, 0, taille, taille).data; } catch (e) { callback([]); return; }
        var compteur = {};
        for (var i = 0; i < data.length; i += 4) {
          var r = Math.round(data[i] / 32) * 32, g = Math.round(data[i + 1] / 32) * 32, b = Math.round(data[i + 2] / 32) * 32;
          var cle = r + "," + g + "," + b; compteur[cle] = (compteur[cle] || 0) + 1;
        }
        var tries = Object.keys(compteur).sort(function (a, b) { return compteur[b] - compteur[a]; }).slice(0, 5);
        callback(tries.map(function (c) { return "rgb(" + c + ")"; }));
      };
      img.onerror = function () { callback([]); };
      img.src = dataUrl;
    }
    function afficherPaletteExtraite(conteneurId, dataUrl) {
      var conteneur = root.querySelector("#" + conteneurId);
      if (!conteneur) return;
      extraireCouleursDominantes(dataUrl, function (couleurs) {
        if (!couleurs.length) { conteneur.hidden = true; return; }
        conteneur.hidden = false;
        conteneur.innerHTML = '<span class="pers-palette-extraite__label">' + (t("extracted_palette_label") || "Couleurs dominantes détectées (indicatif)") + '</span>' +
          couleurs.map(function (c) { return '<span class="pers-swatch-dot" style="background:' + c + '"></span>'; }).join("");
      });
    }

    var MOIS_HEGIRIENS = ["Mouharram", "Safar", "Rabi al-Awwal", "Rabi al-Thani", "Joumada al-Oula", "Joumada al-Thania", "Rajab", "Chaabane", "Ramadan", "Chawwal", "Dhou al-Qi'da", "Dhou al-Hijja"];
    function gregorienVersHijri(date) {
      var jd = Math.floor((1461 * (date.getFullYear() + 4800 + Math.floor((date.getMonth() + 1 - 14) / 12))) / 4) +
        Math.floor((367 * (date.getMonth() + 1 - 2 - 12 * Math.floor((date.getMonth() + 1 - 14) / 12))) / 12) -
        Math.floor((3 * Math.floor((date.getFullYear() + 4900 + Math.floor((date.getMonth() + 1 - 14) / 12)) / 100)) / 4) + date.getDate() - 32075;
      var l = jd - 1948440 + 10632;
      var n = Math.floor((l - 1) / 10631);
      l = l - 10631 * n + 354;
      var j = Math.floor((10985 - l) / 5316) * Math.floor((50 * l) / 17719) + Math.floor(l / 5670) * Math.floor((43 * l) / 15238);
      l = l - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) - Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
      var mois = Math.floor((24 * l) / 709);
      var jour = l - Math.floor((709 * mois) / 24);
      var annee = 30 * n + j - 30;
      return { jour: jour, mois: MOIS_HEGIRIENS[mois - 1] || "", annee: annee };
    }

    var DICTIONNAIRE_PRENOMS = {
      "amina": "أمينة", "aicha": "عائشة", "fatima": "فاطمة", "khadija": "خديجة", "maryam": "مريم", "zeynab": "زينب", "hafsa": "حفصة", "sumaya": "سمية",
      "mohamed": "محمد", "mohammed": "محمد", "ahmed": "أحمد", "ali": "علي", "omar": "عمر", "youssef": "يوسف", "ibrahim": "إبراهيم", "bilal": "بلال", "hamza": "حمزة",
      "yasmine": "ياسمين", "nour": "نور", "sara": "سارة", "leila": "ليلى", "imane": "إيمان", "amine": "أمين", "salma": "سلمى", "yasser": "ياسر"
    };
    function suggererTranslitteration(nom) { var cle = normaliserTexte(nom || "").trim(); return DICTIONNAIRE_PRENOMS[cle] || null; }
    var boutonTranslit = root.querySelector("#pers-translit-btn");
    var resultatTranslit = root.querySelector("#pers-translit-result");
    if (boutonTranslit) {
      boutonTranslit.addEventListener("click", function () {
        var champNomDigital = root.querySelector("#pers-digital-name");
        var suggestion = suggererTranslitteration(champNomDigital ? champNomDigital.value : "");
        if (resultatTranslit) {
          resultatTranslit.hidden = false;
          resultatTranslit.textContent = suggestion ? (t("translit_found") || "Suggestion :") + " " + suggestion : (t("translit_not_found") || "Prénom non reconnu dans notre liste limitée — contactez-nous pour une transcription sur mesure.");
        }
      });
    }

    function majUpsellKit() {
      var upsellBlock = root.querySelector("#pers-upsell-kit");
      if (!upsellBlock) return;
      upsellBlock.hidden = !(state.flow === "etiquette-cdv" || state.flow === "pod-image");
    }

    showStep("1", { viaBack: true });
    state.history = [];
  }

  function init(racine) { (racine || document).querySelectorAll(".pers-module").forEach(initModule); }
  if (document.readyState === "loading") { document.addEventListener("DOMContentLoaded", function () { init(document); }); } else { init(document); }
  document.addEventListener("shopify:section:load", function (evt) { if (evt.target && evt.target.querySelector) init(evt.target); });
})();
